<template>
    <ul class="pagination" data-testid="pagination">
        <template v-for="item in paginationItems" :key="item.page">
            <li v-if="item.showLeftEllipsis">
                <button class="pagination__item pagination__ellipsis" @click="handleLeftEllipsisClick">...</button>
            </li>
            <li v-show="item.isVisible">
                <NuxtLink
                    :data-testid="`pagination-link-${item.page}`"
                    :external="external"
                    :class="['pagination__item', {'pagination__item--active': item.page === currentPage}]"
                    :to="{query: item.page === 1 ? rest : {...rest, p: item.page}}"
                >
                    {{ item.page }}
                </NuxtLink>
            </li>
            <li v-if="item.showRightEllipsis">
                <button class="pagination__item pagination__ellipsis" @click="handleRightEllipsisClick">...</button>
            </li>
        </template>
    </ul>
</template>

<script setup lang="ts">
    import {computed, ref, watch} from 'vue';
    import {useRoute} from '#imports';

    const props = defineProps<{
        currentPage: number;
        totalPages: number;
        external?: boolean;
    }>();

    const route = useRoute();
    const {p, ...rest} = route.query;

    const {allAvailablePages, neighborPages} = useAvailablePages(props);
    const {showLeftPages, showRightPages, handleLeftEllipsisClick, handleRightEllipsisClick} = useEllipsisNavigation();
    const {paginationItems} = usePaginationItems();

    function useAvailablePages(props: {currentPage: number; totalPages: number}) {
        const ANCHOR_STEP = 10;

        const firstAndLastPages = computed(() => [1, props.totalPages]);

        const neighborPages = computed((): number[] => {
            const neighbors = [props.currentPage];

            if (props.currentPage > 1) {
                neighbors.push(props.currentPage - 1);
            }

            if (props.currentPage < props.totalPages) {
                neighbors.push(props.currentPage + 1);
            }

            return neighbors;
        });

        const anchorPages = computed((): number[] => {
            const anchors: number[] = [];
            const numAnchors = ~~(props.totalPages / ANCHOR_STEP);
            Array.from({length: numAnchors}).forEach((_, idx) => {
                anchors.push((idx + 1) * ANCHOR_STEP);
            });

            return anchors;
        });

        const blockRange = computed((): number[] => {
            const blockRange: number[] = [];

            const isOnAnchorPage = props.currentPage % ANCHOR_STEP === 0;

            const rawStart = isOnAnchorPage ? props.currentPage - 1 : ~~((props.currentPage - 1) / ANCHOR_STEP) * ANCHOR_STEP;
            const rawEnd = isOnAnchorPage ? props.currentPage + 9 : rawStart + 9;

            const start = Math.max(1, rawStart);
            const end = Math.min(props.totalPages, rawEnd);

            const rangeLength = end - start + 1;
            Array.from({length: rangeLength}).forEach((_, i) => {
                blockRange.push(start + i);
            });

            return blockRange;
        });

        const allAvailablePages = computed((): number[] => {
            const pages = new Set<number>();
            [...firstAndLastPages.value, ...anchorPages.value, ...blockRange.value].forEach((page) => pages.add(page));
            return Array.from(pages).sort((a, b) => a - b);
        });

        return {
            allAvailablePages,
            neighborPages,
        };
    }

    function usePaginationItems() {
        type Page = {
            page: number;
            showLeftEllipsis: boolean;
            showRightEllipsis: boolean;
            isVisible: boolean;
        };

        function isPageVisible(page: number) {
            return (
                page === 1 ||
                page === props.totalPages ||
                neighborPages.value.includes(page) ||
                (showLeftPages.value && page < props.currentPage) ||
                (showRightPages.value && page > props.currentPage)
            );
        }

        const paginationItems = computed(() => {
            const pages: Page[] = [];

            allAvailablePages.value.forEach((page, index) => {
                const showLeftEllipsis =
                    !showLeftPages.value && ((page > 2 && page <= props.currentPage && index === 1) || (props.currentPage > 3 && page === 2));

                const isLastPages =
                    props.totalPages - 1 === props.currentPage ||
                    props.currentPage === props.totalPages - 2 ||
                    props.currentPage === props.totalPages;

                const showRightEllipsis = !showRightPages.value && allAvailablePages.value[index + 1] === props.totalPages && !isLastPages;

                pages.push({
                    page,
                    showLeftEllipsis,
                    showRightEllipsis,
                    isVisible: isPageVisible(page),
                });
            });
            return pages;
        });

        return {paginationItems};
    }

    function useEllipsisNavigation() {
        const showLeftPages = ref(false);
        const showRightPages = ref(false);

        watch(
            () => props.currentPage,
            () => {
                showLeftPages.value = false;
                showRightPages.value = false;
            },
        );

        function handleLeftEllipsisClick() {
            showLeftPages.value = true;
            showRightPages.value = false;
        }

        function handleRightEllipsisClick() {
            showLeftPages.value = false;
            showRightPages.value = true;
        }

        return {
            showLeftPages,
            showRightPages,
            handleLeftEllipsisClick,
            handleRightEllipsisClick,
        };
    }
</script>

<style lang="scss">
    .pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--joy-core-spacing-2);
        margin-top: var(--joy-core-spacing-6);

        li {
            position: relative;
            list-style-type: none;
        }

        &__item {
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: var(--joy-color-neutral-40);
            font-size: var(--joy-font-size-primary-600);
            font-weight: var(--joy-font-weight-bold);
            border-radius: var(--joy-core-radius-6);
            user-select: none;
            text-decoration: none;
            width: 36px;
            height: 36px;

            &::after {
                content: '';
                position: absolute;
                left: -1px;
                top: -1px;
                width: calc(100% + 2px);
                height: calc(100% + 2px);
                transition: box-shadow var(--joy-transition-duration-default);
                border-radius: var(--joy-core-radius-6);
            }

            &:focus::after {
                box-shadow: var(--joy-form-field-focus-shadow);
            }

            &:hover:not(&--active) {
                background-color: var(--joy-color-secondary-10);
                color: var(--joy-color-secondary-30);
            }

            &--active {
                background-color: var(--joy-color-secondary-30);
                color: var(--joy-color-neutral-0);
            }
        }

        &__ellipsis {
            &:hover {
                background-color: var(--joy-color-neutral-20);
            }
        }
    }
</style>
